import { ZIP_5_DIGIT_REGEX, ZIP_9_DIGIT_REGEX } from "@src/constants";
import { fetchAddressSearch } from "@src/services";
import ClientLogsService from "@src/services/client-logs.service";
import { useCallback, useEffect, useRef, useState } from "react";

interface AddressPrefillLabels {
  errorInvalidZip: string;
}

interface AddressPrefillParams {
  data: {
    canvasPrefillVersion?: number;
    testRecord?: boolean;
    userUuid?: string;
    zip?: string;
  };
  valueZip: string;
  resolvedUserRole?: string;
  labels: AddressPrefillLabels;
  setValue: React.Dispatch<React.SetStateAction<any>>;
  setData: (key: string, value: any) => void;
  setErrors: React.Dispatch<React.SetStateAction<any>>;
  resetPolicies: () => void;
  setInsuranceFetchKey: React.Dispatch<React.SetStateAction<number>>;
}

export const getAddressSearchErrorMessage = (response: any): string =>
  response?.error || response?.message || "Address search failed";

const toAddressObj = (addressResponse: any) => {
  if (!addressResponse?.context_id || !addressResponse?.state_code) {
    return null;
  }

  return {
    city: addressResponse.city || "",
    state: addressResponse.state || "",
    position: addressResponse.position || { lat: 0, lon: 0 },
    state_code: addressResponse.state_code,
    context_id: addressResponse.context_id,
  };
};

const formatZip = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, 9);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
};

export const useAddressPrefill = ({
  data,
  valueZip,
  resolvedUserRole,
  labels,
  setValue,
  setData,
  setErrors,
  resetPolicies,
  setInsuranceFetchKey,
}: AddressPrefillParams) => {
  const [addressData, setAddressData] = useState<any>(null);
  const [resolvedZip, setResolvedZip] = useState<string | null>(null);
  const prefillTriggeredRef = useRef(false);
  const initialPrefillZipRef = useRef(typeof data.zip === "string" ? data.zip : "");
  const currentZipRef = useRef(valueZip);
  const addressSearchRequestRef = useRef<symbol | null>(null);
  // Stryker disable next-line ObjectLiteral: current is replaced during render before any effect reads it.
  const canvasPrefillPayloadRef = useRef({ zip: data.zip, valueZip });

  canvasPrefillPayloadRef.current = { zip: data.zip, valueZip };

  useEffect(() => {
    if (!data.canvasPrefillVersion) {
      return;
    }

    const { zip: canvasZip, valueZip: canvasValueZip } = canvasPrefillPayloadRef.current;
    const nextZip = canvasZip || canvasValueZip;
    currentZipRef.current = nextZip;
    setAddressData(null);
    setResolvedZip(null);
    if (nextZip && nextZip === canvasZip) {
      initialPrefillZipRef.current = nextZip;
      prefillTriggeredRef.current = false;
    }
    // Only a new Canvas event should reset prefill state. A successful manual
    // ZIP lookup persists its ZIP to ApplicationContext too, which must not be
    // treated as another Canvas prefill event.
  }, [data.canvasPrefillVersion]);

  useEffect(() => {
    let isCancelled = false;

    const applyPrefillAddressResponse = (addressResponse: any, zipValue: string) => {
      if (addressResponse && !(addressResponse as any).error) {
        const addressObj = toAddressObj(addressResponse);
        if (addressObj) {
          setAddressData(addressResponse);
          setResolvedZip(zipValue);
          setValue((prevData) => ({ ...prevData, address: addressObj }));
          setData("zip", zipValue);
          setData("address", addressObj);
          setData("userRecordCreated", true);
          return;
        }

        setErrors((prev) => ({ ...prev, zip: labels.errorInvalidZip }));
        ClientLogsService.logError(
          data.userUuid || "unknown",
          "GetStartedForm.prefillAddressSearch",
          `Prefill address-search returned incomplete address for zip: ${zipValue}`,
          new Error("Missing context_id or state_code in address response"),
        );
        setResolvedZip(null);
        return;
      }

      setErrors((prev) => ({ ...prev, zip: labels.errorInvalidZip }));
      if (!addressResponse) {
        ClientLogsService.logError(
          data.userUuid || "unknown",
          "GetStartedForm.prefillAddressSearch",
          `Prefill address-search failed for zip: ${zipValue}`,
          new Error("No address response from address search"),
          {
            errorType: "UnexpectedSystemFailure",
            functionName: "applyPrefillAddressResponse",
          },
        );
      }
      setResolvedZip(null);
    };

    const runPrefill = async () => {
      const zipValue = valueZip;
      const rawValue = zipValue.replace(/\D/g, "");
      const shouldSkip =
        prefillTriggeredRef.current ||
        !initialPrefillZipRef.current ||
        zipValue !== initialPrefillZipRef.current ||
        !zipValue ||
        !data.userUuid ||
        (!ZIP_5_DIGIT_REGEX.test(rawValue) && !ZIP_9_DIGIT_REGEX.test(zipValue));

      if (shouldSkip) {
        return;
      }

      prefillTriggeredRef.current = true;
      const requestId = Symbol("address-search");
      addressSearchRequestRef.current = requestId;
      currentZipRef.current = zipValue;

      try {
        const addressResponse = await fetchAddressSearch(
          zipValue,
          data.userUuid,
          resolvedUserRole,
          data.testRecord,
        );

        if (
          isCancelled ||
          requestId !== addressSearchRequestRef.current ||
          currentZipRef.current !== zipValue
        ) {
          prefillTriggeredRef.current = false;
          return;
        }

        applyPrefillAddressResponse(addressResponse, zipValue);
      } catch (err) {
        setResolvedZip(null);
        ClientLogsService.logError(
          data.userUuid || "unknown",
          "GetStartedForm.prefillAddressSearch",
          `Prefill address-search failed for zip: ${zipValue}`,
          err instanceof Error ? err : new Error(String(err)),
        );
        if (isCancelled) {
          prefillTriggeredRef.current = false;
        }
      }
    };

    runPrefill();

    return () => {
      isCancelled = true;
    };
  }, [
    data.testRecord,
    data.canvasPrefillVersion,
    data.userUuid,
    labels.errorInvalidZip,
    resolvedUserRole,
    setData,
    setErrors,
    setValue,
    valueZip,
  ]);

  const handleZipChange = useCallback(
    async (fieldValue: string) => {
      const rawValue = fieldValue.replace(/\D/g, "");
      const formattedValue = formatZip(fieldValue);
      const requestId = Symbol("address-search");
      addressSearchRequestRef.current = requestId;
      currentZipRef.current = formattedValue;

      resetPolicies();
      setAddressData(null);
      setResolvedZip(null);
      setData("address", undefined);
      setValue((prev) => ({
        ...prev,
        zip: formattedValue,
        address: null,
        selectedEnrollmentCategory: null,
        selectedPolicy: null,
        insurancePolicy: {},
      }));
      setErrors((prev) => ({ ...prev, selectedPolicy: "", zip: "" }));

      const isValidZip = ZIP_5_DIGIT_REGEX.test(rawValue) || ZIP_9_DIGIT_REGEX.test(formattedValue);
      if (!isValidZip || !data.userUuid) return;

      const addressResponse = await fetchAddressSearch(
        formattedValue,
        data.userUuid,
        resolvedUserRole,
        data.testRecord,
      );

      if (
        requestId !== addressSearchRequestRef.current ||
        currentZipRef.current !== formattedValue
      ) {
        return;
      }

      if (addressResponse && !(addressResponse as any).error) {
        const addressObj = toAddressObj(addressResponse);
        setAddressData(addressResponse);
        if (addressObj) {
          setResolvedZip(formattedValue);
          setErrors((prev) => ({ ...prev, zip: "" }));
          setValue((prev) => ({ ...prev, address: addressObj }));
          setData("zip", formattedValue);
          setData("address", addressObj);
          setData("userRecordCreated", true);
          setInsuranceFetchKey((key) => key + 1);
        } else {
          setResolvedZip(null);
          setErrors((prev) => ({ ...prev, zip: labels.errorInvalidZip }));
          setAddressData(null);
          resetPolicies();
        }
      } else {
        setResolvedZip(null);
        if (!(addressResponse as any)?.error) {
          ClientLogsService.logError(
            data.userUuid || "unknown",
            "GetStartedForm.handleChange",
            `Failed to fetch address for zip code: ${formattedValue}`,
            new Error(getAddressSearchErrorMessage(addressResponse)),
            {
              errorType: "UnexpectedSystemFailure",
              functionName: "handleZipChange",
            },
          );
        }
        setAddressData(null);
        resetPolicies();
        setValue((prev) => ({
          ...prev,
          selectedEnrollmentCategory: null,
          selectedPolicy: null,
          insurancePolicy: {},
        }));
        setErrors((prev) => ({
          ...prev,
          zip: labels.errorInvalidZip,
          selectedPolicy: "",
        }));
      }
    },
    [
      data.testRecord,
      data.userUuid,
      labels.errorInvalidZip,
      resetPolicies,
      resolvedUserRole,
      setData,
      setErrors,
      setInsuranceFetchKey,
      setValue,
    ],
  );

  return { addressData, resolvedZip, handleZipChange };
};
